import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "animate", "Content", "renderContent", "disabled", "flush", "isCardHeader", "onOpenChange", "open", "title", "size", "titleTagName", "defaultOpen", "contentStyles", "removeContentPadding"];
import { toShorthandSize } from '../../utils/tShirtSizes';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import styled from 'foundations-theming-specialty/utils';
import { sizeToHeading, getPaddingLeft, getMarginBottom, getIconSize } from './AccordionItem.helpers';
import { useDisclosure } from '../../../hooks/useDisclosure';
import { Clickable } from '../Clickable/Clickable';
import { AccordionTransition } from '../AccordionTransition';
import { setUiTransition } from '../../utils/styles/styles';
import { WIDTH } from '../../../constants/nonTokenisedValues';
import { getFocusPseudoClass, getFocusStyleObject } from '../../utils/focus/focus.style';
import { SUN_CORE_LAYER } from '../../constants/Layers';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledClickable = styled(Clickable)(({
  $size,
  $titleTagName,
  $isCardHeader,
  theme
}) => {
  const paddingLeftToken = getPaddingLeft($size, $titleTagName);
  return {
    position: 'relative',
    display: 'block',
    userSelect: 'none',
    paddingInlineStart: theme.space[paddingLeftToken],
    marginBottom: $isCardHeader ? theme.space['0'] : theme.space[getMarginBottom($size, $titleTagName)],
    '> h1, > h2, > h3, > h4, > h5, > h6, > span': {
      '&:hover': {
        color: theme.color['specialty-text-interactive-alt-hover']
      }
    },
    [getFocusPseudoClass()]: Object.assign({}, getFocusStyleObject({
      theme,
      override: true
    }), {
      '&::after': Object.assign({
        // Must move focus ring outside arrow, which is outside the button bounding box.
        // Must also account for anything flush on the right, top, and bottom sides
        // otherwise the focus ring will be under those flush elements. We move
        // the focus ring the width of the border outside the bounding box.
        width: `calc(100% + ${theme.space['200']} + ${theme.borderWidth['200']})`,
        height: `calc(100% + calc(${theme.borderWidth['200']} * 2))`,
        position: 'absolute',
        top: `calc(${theme.borderWidth['200']} * -1)`,
        left: `calc(calc(${theme.space['200']} * -1) + ${theme.borderWidth['200']})`,
        right: `calc(${theme.borderWidth['200']} * -1 )`,
        bottom: `calc(${theme.borderWidth['200']} * -1)`,
        content: '""',
        zIndex: SUN_CORE_LAYER
      }, getFocusStyleObject({
        theme,
        unwrapped: true
      }))
    })
  };
});

// The `as` prop will determine the tag name that is used
const StyledHeading = styled.span.withConfig({
  displayName: "AccordionItem__StyledHeading",
  componentId: "sc-7rt9xe-0"
})(({
  $flush,
  theme
}) => {
  return Object.assign({}, $flush && {
    marginBottom: `${theme.space['0']} !important`
  });
});
const StyledCaretWrapper = styled.div.withConfig({
  displayName: "AccordionItem__StyledCaretWrapper",
  componentId: "sc-7rt9xe-1"
})(({
  $open = false,
  theme
}) => {
  const transform = $open ? 'rotate(90deg)' : undefined;
  return Object.assign({
    transform,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'start',
    position: 'absolute',
    left: theme.space[0],
    top: theme.space[0],
    width: WIDTH.ACCORDION_ITEM_CARET
  }, setUiTransition());
});
const ExpandableCaret = ({
  open,
  size,
  titleTagName
}) => {
  return /*#__PURE__*/_jsx(StyledCaretWrapper, {
    $open: open,
    $size: size,
    $titleTagName: titleTagName,
    children: /*#__PURE__*/_jsx("span", {
      children: /*#__PURE__*/_jsx(RightIcon, {
        color: "icon-interactive-default",
        size: getIconSize(size, titleTagName)
      })
    })
  });
};
const StyledAccordionItemWrapper = styled.div.withConfig({
  displayName: "AccordionItem__StyledAccordionItemWrapper",
  componentId: "sc-7rt9xe-2"
})(({
  $cssStyles
}) => Object.assign({}, $cssStyles));
const AccordionItemWrapper = styled.div.withConfig({
  displayName: "AccordionItem__AccordionItemWrapper",
  componentId: "sc-7rt9xe-3"
})(({
  $contentCSSStyles
}) => {
  return Object.assign({}, $contentCSSStyles);
});
export const AccordionItem = _ref => {
  let {
      children,
      animate = true,
      Content,
      renderContent,
      disabled = false,
      flush = false,
      isCardHeader,
      onOpenChange,
      open = undefined,
      title,
      size = 'xs',
      titleTagName,
      defaultOpen,
      contentStyles = {},
      removeContentPadding = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const contentCSSStyles = useMapTokenPropsToCSS(contentStyles);

  // isOpen is a controlled state if open is provided, else the state is handled by useDisclosure
  const {
    isOpen,
    onOpen,
    onClose,
    triggerElementAriaAttributes
  } = useDisclosure({
    defaultIsOpen: defaultOpen || open,
    disabled,
    isOpen: open,
    onOpen: () => {
      onOpenChange === null || onOpenChange === void 0 || onOpenChange(true);
    },
    onClose: () => {
      onOpenChange === null || onOpenChange === void 0 || onOpenChange(false);
    }
  });
  const shorthandSize = toShorthandSize(size);
  return /*#__PURE__*/_jsxs(StyledAccordionItemWrapper, Object.assign({}, domProps, {
    $cssStyles: cssStyles,
    children: [/*#__PURE__*/_jsx(StyledClickable, Object.assign({}, triggerElementAriaAttributes, {
      $size: size,
      $titleTagName: titleTagName,
      $isCardHeader: isCardHeader,
      disabled: disabled,
      onClick: () => {
        if (isOpen) {
          onClose();
        } else {
          onOpen();
        }
      },
      children: /*#__PURE__*/_jsxs(StyledHeading, {
        as: titleTagName || sizeToHeading[size],
        role: "presentation",
        $flush: flush,
        children: [/*#__PURE__*/_jsx(ExpandableCaret, {
          open: isOpen,
          size: size,
          titleTagName: titleTagName
        }), title]
      })
    })), /*#__PURE__*/_jsx(AccordionTransition, {
      Content: Content,
      duration: animate ? undefined : 0,
      open: isOpen,
      renderContent: renderContent // cast shouldn't be necessary but renderContent was declared too generically in AccordionItem
      ,
      paddingInlineStart: removeContentPadding ? '0' : getPaddingLeft(size, titleTagName),
      children: /*#__PURE__*/_jsx(AccordionItemWrapper, {
        $contentCSSStyles: contentCSSStyles,
        $size: shorthandSize,
        $titleTagName: titleTagName,
        children: children
      })
    })]
  }));
};
AccordionItem.displayName = 'AccordionItem';